/**
 * Shared timeseries types for API contracts between frontend and backend
 * V2 Architecture: Raw (events) -> Observations (aggregated) -> Forecasts (predictions)
 */
import { z } from "zod";
import { uuidSchema } from "./validation-common.js";
// ===== ENUMS & CONSTANTS =====
/**
 * Granularity for timeseries data aggregation
 * - half_hourly: Aggregates data by 30-minute intervals
 * - daily: Aggregates data by day
 * - weekly: Aggregates data by week
 */
export const GranularitySchema = z.enum(["half_hourly", "daily", "weekly"]);
/**
 * Aggregation methods for computing Observations from Raw data
 * - SUM: Sum all values in the time bucket (e.g., total sales)
 * - AVG: Average all values in the time bucket (e.g., satisfaction score)
 * - COUNT: Count number of data points in the time bucket (e.g., transaction count)
 * - DERIVED: Computed from other series (e.g., productivity = sales / labor hours)
 */
export const AggregationMethodSchema = z.enum(["SUM", "AVG", "COUNT", "DERIVED"]);
/**
 * Unit types for series values
 */
export const SeriesUnitSchema = z.enum([
    "dollars",
    "dollars/hour",
    "count",
    "percentage",
    "percent",
    "score",
    "hours",
    "minutes",
    "derived",
]);
/**
 * Global registry of all supported series types
 * Platform-level definitions - companies can enable/disable but not modify
 * NOTE: Only includes series that can currently be computed from available data
 */
export const SERIES_CONFIGS = {
    // ===== BASE SERIES (get ARIMA + Seasonal Naive forecasts) =====
    transaction_total: {
        seriesType: "transaction_total",
        displayName: "Total Sales",
        description: "Sum of transaction amounts (excluding tips, taxes, discounts)",
        unit: "dollars",
        aggregationMethod: "SUM",
        crossPeriodAggregation: "SUM",
        supportsEmployeeScope: false,
        forecast: {
            enabled: true,
            models: ["arima", "seasonal_naive"],
        },
    },
    transaction_tip: {
        seriesType: "transaction_tip",
        displayName: "Total Tip Amount",
        description: "Sum of tip amounts from transactions",
        unit: "dollars",
        aggregationMethod: "SUM",
        crossPeriodAggregation: "SUM",
        supportsEmployeeScope: false,
        forecast: {
            enabled: true,
            models: ["arima", "seasonal_naive"],
        },
    },
    transaction_count: {
        seriesType: "transaction_count",
        displayName: "Total Transactions",
        description: "Number of transactions processed",
        unit: "count",
        aggregationMethod: "COUNT",
        crossPeriodAggregation: "SUM",
        supportsEmployeeScope: false,
        forecast: {
            enabled: true,
            models: ["arima", "seasonal_naive"],
        },
    },
    labor_hours: {
        seriesType: "labor_hours",
        displayName: "Total Labor Hours",
        description: "Total hours worked",
        unit: "hours",
        aggregationMethod: "SUM",
        crossPeriodAggregation: "SUM",
        supportsEmployeeScope: true,
        forecast: {
            enabled: true,
            models: ["arima", "seasonal_naive"],
        },
    },
    labor_cost: {
        seriesType: "labor_cost",
        displayName: "Total Labor Costs",
        description: "Total labor costs based on hours worked and base pay rate",
        unit: "dollars",
        aggregationMethod: "SUM",
        crossPeriodAggregation: "SUM",
        supportsEmployeeScope: true,
        forecast: {
            enabled: true,
            models: ["arima", "seasonal_naive"],
        },
    },
    punctuality: {
        seriesType: "punctuality",
        displayName: "Punctuality",
        description: "Minutes late/early compared to scheduled shift start (negative = early)",
        unit: "minutes",
        aggregationMethod: "AVG",
        crossPeriodAggregation: "AVERAGE",
        supportsEmployeeScope: true,
        // No forecast - historical metric only
    },
    ot_hours: {
        seriesType: "ot_hours",
        displayName: "Total OT Hours",
        description: "Hours worked beyond standard threshold",
        unit: "hours",
        aggregationMethod: "SUM",
        crossPeriodAggregation: "SUM",
        supportsEmployeeScope: true,
        // No forecast - dependent on scheduling decisions
    },
    schedule_diff_minutes: {
        seriesType: "schedule_diff_minutes",
        displayName: "Schedule vs Worked (min)",
        description: "Difference in minutes between scheduled shift and actual clock time (half-hourly)",
        unit: "minutes",
        aggregationMethod: "SUM",
        crossPeriodAggregation: "SUM",
        supportsEmployeeScope: true,
        // No forecast - historical metric only
    },
    labor_forecast_by_job_role_family: {
        seriesType: "labor_forecast_by_job_role_family",
        displayName: "Labor Forecast (Job)",
        description: "Forecasted labor hours required by job role family based on staffing ratios",
        unit: "hours",
        aggregationMethod: "SUM",
        crossPeriodAggregation: "SUM",
        supportsEmployeeScope: false,
        // This IS a forecast output, not something we forecast again
    },
    // ===== DERIVED SERIES (forecasts computed from base forecasts) =====
    productivity: {
        seriesType: "productivity",
        displayName: "Productivity (Sales / Employee Hours)",
        description: "Sales per labor hour (transaction_total / labor_hours)",
        unit: "dollars/hour",
        aggregationMethod: "DERIVED",
        crossPeriodAggregation: "AVERAGE",
        supportsEmployeeScope: false,
        forecast: {
            enabled: true,
            dependsOnForecasts: ["transaction_total", "labor_hours"],
        },
    },
    tip_percent: {
        seriesType: "tip_percent",
        displayName: "Tip as % of Sales",
        description: "Tips as a percentage of sales (transaction_tip / transaction_total * 100)",
        unit: "percent",
        aggregationMethod: "DERIVED",
        crossPeriodAggregation: "AVERAGE",
        supportsEmployeeScope: false,
        forecast: {
            enabled: true,
            dependsOnForecasts: ["transaction_tip", "transaction_total"],
        },
    },
    average_transaction_amount: {
        seriesType: "average_transaction_amount",
        displayName: "Avg Transaction Amount",
        description: "Average value per transaction (transaction_total / transaction_count)",
        unit: "dollars",
        aggregationMethod: "DERIVED",
        crossPeriodAggregation: "AVERAGE",
        supportsEmployeeScope: false,
        forecast: {
            enabled: true,
            dependsOnForecasts: ["transaction_total", "transaction_count"],
        },
    },
    average_daily_sales: {
        seriesType: "average_daily_sales",
        displayName: "Avg Daily Sales",
        description: "Average sales per day in the time bucket",
        unit: "dollars",
        aggregationMethod: "DERIVED",
        crossPeriodAggregation: "AVERAGE",
        supportsEmployeeScope: false,
        forecast: {
            enabled: true,
            dependsOnForecasts: ["transaction_total"],
        },
    },
    labor_percent_of_sales: {
        seriesType: "labor_percent_of_sales",
        displayName: "Labor as % of Sales",
        description: "Labor costs as a percentage of sales (labor_cost / transaction_total * 100)",
        unit: "percent",
        aggregationMethod: "DERIVED",
        crossPeriodAggregation: "AVERAGE",
        supportsEmployeeScope: false,
        forecast: {
            enabled: true,
            dependsOnForecasts: ["labor_cost", "transaction_total"],
        },
    },
    average_tip_amount: {
        seriesType: "average_tip_amount",
        displayName: "Avg Tip Amount",
        description: "Average tip per transaction (transaction_tip / transaction_count)",
        unit: "dollars",
        aggregationMethod: "DERIVED",
        crossPeriodAggregation: "AVERAGE",
        supportsEmployeeScope: false,
        forecast: {
            enabled: true,
            dependsOnForecasts: ["transaction_tip", "transaction_count"],
        },
    },
    average_daily_tips: {
        seriesType: "average_daily_tips",
        displayName: "Avg Daily Tips",
        description: "Average tips per day in the time bucket",
        unit: "dollars",
        aggregationMethod: "DERIVED",
        crossPeriodAggregation: "AVERAGE",
        supportsEmployeeScope: false,
        forecast: {
            enabled: true,
            dependsOnForecasts: ["transaction_tip"],
        },
    },
    average_daily_transactions: {
        seriesType: "average_daily_transactions",
        displayName: "Avg Daily Transactions",
        description: "Average transaction count per day in the time bucket",
        unit: "count",
        aggregationMethod: "DERIVED",
        crossPeriodAggregation: "AVERAGE",
        supportsEmployeeScope: false,
        forecast: {
            enabled: true,
            dependsOnForecasts: ["transaction_count"],
        },
    },
    average_daily_labor_hours: {
        seriesType: "average_daily_labor_hours",
        displayName: "Avg Daily Labor Hours",
        description: "Average labor hours per day in the time bucket",
        unit: "hours",
        aggregationMethod: "DERIVED",
        crossPeriodAggregation: "AVERAGE",
        supportsEmployeeScope: false,
        forecast: {
            enabled: true,
            dependsOnForecasts: ["labor_hours"],
        },
    },
    average_daily_labor_cost: {
        seriesType: "average_daily_labor_cost",
        displayName: "Avg Daily Labor Cost",
        description: "Average labor cost per day in the time bucket",
        unit: "dollars",
        aggregationMethod: "DERIVED",
        crossPeriodAggregation: "AVERAGE",
        supportsEmployeeScope: false,
        forecast: {
            enabled: true,
            dependsOnForecasts: ["labor_cost"],
        },
    },
    average_daily_ot_hours: {
        seriesType: "average_daily_ot_hours",
        displayName: "Avg Daily OT Hours",
        description: "Average overtime hours per day in the time bucket",
        unit: "hours",
        aggregationMethod: "DERIVED",
        crossPeriodAggregation: "AVERAGE",
        supportsEmployeeScope: false,
        // No forecast - ot_hours base series has no forecast models enabled
    },
};
/**
 * Transaction-based series types that are extracted from Square orders
 * These series require checkpoints for aggregation scheduling
 */
export const TRANSACTION_SERIES_TYPES = [
    "transaction_total",
    "transaction_tip",
    "transaction_count",
];
/**
 * Labor-based series types that are extracted from timecard data
 * These series require checkpoints for aggregation scheduling
 */
export const LABOR_SERIES_TYPES = ["labor_hours", "labor_cost", "punctuality", "ot_hours", "schedule_diff_minutes"];
/**
 * Derived series types that are computed from other series
 * These do not have raw data points - they are computed during aggregation
 */
export const DERIVED_SERIES_TYPES = [
    "productivity",
    "tip_percent",
    "average_transaction_amount",
    "average_daily_sales",
    "labor_percent_of_sales",
    "average_tip_amount",
    "average_daily_tips",
    "average_daily_transactions",
    "average_daily_labor_hours",
    "average_daily_labor_cost",
    "average_daily_ot_hours",
];
/**
 * Series types that get direct ARIMA/Seasonal Naive forecasts
 * These are base series with models defined in their forecast config
 */
export const BASE_FORECAST_SERIES_TYPES = Object.entries(SERIES_CONFIGS)
    .filter(([, config]) => {
    const cfg = config;
    return cfg.forecast?.enabled && cfg.forecast?.models;
})
    .map(([key]) => key);
/**
 * Series types that get derived forecasts (computed from base forecasts)
 * These are derived series with dependsOnForecasts defined
 */
export const DERIVED_FORECAST_SERIES_TYPES = Object.entries(SERIES_CONFIGS)
    .filter(([, config]) => {
    const cfg = config;
    return cfg.forecast?.enabled && cfg.forecast?.dependsOnForecasts;
})
    .map(([key]) => key);
/**
 * Get forecast dependencies for a derived series
 * Returns undefined for base series or series without forecasts
 */
export function getForecastDependencies(seriesType) {
    const config = SERIES_CONFIGS[seriesType];
    return config?.forecast?.dependsOnForecasts;
}
/**
 * Check if a series has forecast enabled
 */
export function isForecastEnabled(seriesType) {
    const config = SERIES_CONFIGS[seriesType];
    return config?.forecast?.enabled ?? false;
}
/**
 * Check if a series is a derived forecast (computed from other forecasts)
 */
export function isDerivedForecast(seriesType) {
    const config = SERIES_CONFIGS[seriesType];
    return (config?.forecast?.enabled === true &&
        config?.forecast?.dependsOnForecasts !== undefined);
}
/**
 * Get display metadata for a series type
 */
export function getSeriesMetadata(seriesType) {
    const config = SERIES_CONFIGS[seriesType];
    return {
        displayName: config?.displayName ?? seriesType,
        unit: config?.unit ?? "count",
    };
}
export const SeriesTypeSchema = z.enum(Object.keys(SERIES_CONFIGS));
// ===== ZOD VALIDATION SCHEMAS =====
// Schema for Raw Data Point input
export const CreateRawDataPointInputSchema = z.object({
    companyId: uuidSchema,
    locationId: uuidSchema,
    employmentId: uuidSchema.optional(),
    seriesType: z.string().min(1),
    timestamp: z.coerce.date(),
    value: z.number(),
    metadata: z.record(z.string(), z.unknown()).optional(),
});
// Schema for bulk raw data point insertion
export const BulkCreateRawDataPointsInputSchema = z.object({
    dataPoints: z.array(CreateRawDataPointInputSchema).min(1),
});
// Schema for Company Forecast Config
export const CreateCompanyForecastConfigInputSchema = z.object({
    companyId: uuidSchema,
    seriesType: z.string().min(1),
    modelType: z.string().min(1),
    parameters: z.record(z.string(), z.unknown()),
});
export const UpdateCompanyForecastConfigInputSchema = z.object({
    modelType: z.string().min(1).optional(),
    parameters: z.record(z.string(), z.unknown()).optional(),
});
// Schema for querying timeseries data
export const GetTimeseriesQuerySchema = z.object({
    companyId: uuidSchema,
    locationId: uuidSchema,
    employmentId: uuidSchema.optional(),
    seriesTypes: z.array(z.string().min(1)).min(1),
    startDate: z.coerce.date(),
    endDate: z.coerce.date(),
    granularity: GranularitySchema.optional().default("daily"),
    includeForecasts: z.boolean().optional().default(false),
});
// ===== API REQUEST/RESPONSE TYPES FOR ROUTES =====
/**
 * Request schema for GET /api/timeseries endpoint
 * This is the external API contract (uses ISO strings for dates)
 */
export const TimeseriesRequestSchema = z.object({
    locationId: uuidSchema,
    seriesTypes: z.array(z.string().min(1)).min(1),
    startDate: z.string().datetime(),
    endDate: z.string().datetime(),
    modelTypes: z.array(z.string().min(1)).optional(),
    granularity: GranularitySchema.optional(),
});
